import { RpcError } from '@w/rpc'
import { bytesToUlid } from '@w/utils'
import type { QueryClient } from '@w/react-query'
import { createRpcOptionsProxy, type CreateClientRouter } from '@w/rpc'
import type { FlashcardsRpcServer } from '../server/rpc/flashcards-rpc.server'
import type { Tel } from '@w/tel'
import { ReplicaTransportError } from '@w/db-turso-browser-adapter/worker'

/** The server authenticates every rpc (auth request ctx); its 401 is the authoritative "no session" answer.
 * :uc[revoked-session-signs-out]: a 401 for the still-stored sender signs out once; other failures and late
 * answers to an earlier user's request keep the stored user. */
export function signOutOnUnauthorized(fetch: Window['fetch'], auth: { getUserOrNull(): { id: string } | null; signOut(): Promise<void> }, tel: Tel): Window['fetch'] {
  let signingOut: Promise<void> | undefined
  return async (input, init) => {
    const sender = auth.getUserOrNull()?.id
    const response = await fetch(input, init)
    if (response.status === 401 && sender !== undefined && auth.getUserOrNull()?.id === sender) {
      // Not awaited: sign-out joins initialization, which may be waiting on this very response.
      signingOut ??= auth.signOut()
        .catch((error: unknown) => { tel.error(error, { operation: 'session.revoked' }) })
        .finally(() => { signingOut = undefined })
    }
    return response
  }
}

/** Reuse the RPC's retained response; only a first download gates readiness. */
export async function refreshWorkspaceDirectory(options: {
  queryClient: QueryClient; rpc: ReturnType<typeof createFlashcardsRpcOptions>
  storage: Storage; userId: string; tel: Tel; bootTel?: Tel
}) {
  const query = options.rpc.workspace.list.queryOptions()
  const saved = options.storage.getItem(JSON.stringify(['workspace-security', options.userId, 'workspace.list', undefined]))
  if (saved !== null) {
    options.queryClient.syncQueryOptions({ ...query, initialData: JSON.parse(saved) as Awaited<ReturnType<typeof query.queryFn>> })
  }
  const refresh = (saved === null
    ? options.queryClient.fetchQuery(query, { tel: options.bootTel ?? options.tel })
    : options.tel.span({ name: 'workspace.directory.refresh', input: undefined }, refreshTel => options.queryClient.fetchQuery(query, { tel: refreshTel })))
    .catch(error => { options.tel.warn('Workspace directory unavailable', { error }) })
  if (saved === null) await refresh
  return { refreshed: refresh }
}

/**
 * The caller's personal root in a workspace, as its directory entry (`workspace.list`) carries it (db-crdt-sync.spec.md
 * :uc[root-pages-have-ulid-ids]). The id never changes, so the retained entry answers; the list is fetched only for a
 * workspace the retained directory does not hold yet.
 */
export async function directoryPersonalRootId(options: { queryClient: QueryClient; rpc: ReturnType<typeof createFlashcardsRpcOptions>; workspaceId: string; tel: Tel }) {
  const query = options.rpc.workspace.list.queryOptions()
  const find = (list: Awaited<ReturnType<typeof query.queryFn>> | undefined) => list?.workspaces.find(workspace => workspace.id === options.workspaceId)?.personalRootId
  const id = find(options.queryClient.getQuery(query.queryKey)?.state.data as Awaited<ReturnType<typeof query.queryFn>> | undefined) ?? find(await options.queryClient.fetchQuery(query, { tel: options.tel }))
  if (id === undefined) throw new RpcError(403, 'Not a member of this workspace')
  return id
}

/** A cached directory opens the personal workspace while `workspace.list` still runs in the background.
 * After a production reset only that list recreates the personal workspace (server endpoints/workspaces.ts),
 * so a replica exchange answered first is refused 403. Reopen once the directory refresh settled. */
export async function openPersonalWorkspace(spaces: { refresh(workspaceId: string, tel: Tel): Promise<void> }, directory: { refreshed: Promise<unknown> }, userId: string, tel: Tel) {
  try { await spaces.refresh(userId, tel) }
  catch (error) {
    if (!(error instanceof ReplicaTransportError && error.status === 403)) throw error
    await directory.refreshed
    await spaces.refresh(userId, tel)
  }
}

/** The server committed the command but the directory refresh after it failed; `result` is the committed result. */
export class DirectoryRefreshError<Result = unknown> extends Error {
  constructor(readonly result: Result, cause: unknown) { super('Directory refresh failed after the command committed', { cause }) }
}

/** Aborts `selection` on the next real navigation (push, replace, reload or back/forward; not an in-place
 * entry update) until `stop()` is called. A plain event subscription for the life of one pending command,
 * not a React effect: it covers page-to-page param changes, route unmounts and same-URL revisits alike. */
export function abortOnNavigation(selection: AbortController) {
  const onChange = (event: Event) => { if ((event as Event & { navigationType?: string | null }).navigationType !== null) selection.abort() }
  navigation.addEventListener('currententrychange', onChange)
  return { stop: () => { navigation.removeEventListener('currententrychange', onChange) } }
}

/** Malformed field bytes are a protocol refusal, never an offline network fallback. */
function decodeFieldPart(decoder: TextDecoder, bytes: Uint8Array, stream: boolean) {
  try { return decoder.decode(bytes, { stream }) }
  catch (error) {
    if (error instanceof TypeError) throw new RpcError(403, 'Invalid field encoding')
    throw error
  }
}

async function fieldText<Part extends { source: { source: string; sourceEpoch: string; rowid: number; version: number }; field: string; offset: number; total: number; bytes: Uint8Array }>(
  first: Part, source: Part['source'], field: Part['field'], check: () => void, next: (offset: number) => Promise<Part>,
): Promise<string> {
  let part = first
  let offset = 0
  let value = ''
  const decoder = new TextDecoder('utf-8', { fatal: true })
  do {
    if (!part?.source || !source || !(part.bytes instanceof Uint8Array) || part.offset !== offset || part.total !== first.total || part.field !== field || part.source.source !== source.source || part.source.sourceEpoch !== source.sourceEpoch || part.source.rowid !== source.rowid || part.source.version !== source.version || part.bytes.length > part.total - offset || (part.bytes.length === 0 && offset < part.total)) throw new RpcError(403, 'Invalid field part')
    offset += part.bytes.length
    value += decodeFieldPart(decoder, part.bytes, offset < first.total)
    if (offset === first.total) break
    check()
    part = await next(offset)
    check()
  } while (offset < first.total)
  return value
}

/** Both imperative RPC commands and mutation hooks cross the same directory refresh barrier. */
export function refreshAfterDirectoryCommands(client: CreateClientRouter<FlashcardsRpcServer>, refresh: () => Promise<void>, cache?: { storage: Storage; userId: () => string }) {
  function after<Args extends unknown[], Result>(run: (...args: Args) => Promise<Result>) {
    return async (...args: Args) => {
      const result = await run(...args)
      await refresh()
      return result
    }
  }
  function command<T extends { mutate: (...args: never[]) => Promise<unknown> }>(procedure: T) {
    return new Proxy(procedure, { get(target, key, receiver) {
      if (key === 'mutate') return after(target.mutate)
      return Reflect.get(target, key, receiver) as unknown
    } })
  }
  /** Like `command`, but a refresh failure keeps the committed result (DirectoryRefreshError) so callers never repeat a create. */
  function committed<T extends { mutate: (...args: never[]) => Promise<unknown> }>(procedure: T) {
    return new Proxy(procedure, { get(target, key, receiver) {
      if (key !== 'mutate') return Reflect.get(target, key, receiver) as unknown
      return async (...args: Parameters<T['mutate']>) => {
        const result = await target.mutate(...args)
        try { await refresh() } catch (error) { throw new DirectoryRefreshError(result, error) }
        return result
      }
    } })
  }
  function retained<Args extends [unknown?, { signal?: AbortSignal }?], Result>(path: string, run: (...args: Args) => Promise<Result>) {
    return async (...args: Args): Promise<Result> => {
      const principal = cache?.userId()
      const key = cache && JSON.stringify(['workspace-security', principal, path, args[0]])
      try {
        const result = await run(...args)
        if (cache && cache.userId() !== principal) throw new RpcError(403, 'Principal changed')
        if (key) cache?.storage.setItem(key, JSON.stringify(result))
        return result
      } catch (error) {
        args[1]?.signal?.throwIfAborted()
        if (cache && cache.userId() !== principal) throw new RpcError(403, 'Principal changed')
        const saved = key && cache?.storage.getItem(key)
        if (saved && (error instanceof TypeError || error instanceof RpcError && error.status >= 500)) return JSON.parse(saved) as Result
        throw error
      }
    }
  }
  async function sharing(input: Parameters<typeof client.page.sharing.query>[0], ...rest: Tail<Parameters<typeof client.page.sharing.query>>) {
    const principal = cache?.userId()
    const checkPrincipal = () => { rest[0]?.signal?.throwIfAborted(); if (cache && cache.userId() !== principal) throw new RpcError(403, 'Principal changed') }
    const result: { workspaceId: string; pageId: string; principalKind: string; principalId: string; level: number }[] = []
    let cursor: typeof input.cursor
    let coordinate: { securityRevision: number; head: number } | undefined
    do {
      checkPrincipal()
      const response = await client.page.sharing.query({ ...input, cursor }, ...rest)
      checkPrincipal()
      if (coordinate && (coordinate.securityRevision !== response.securityRevision || coordinate.head !== response.head)) throw new RpcError(403, 'Security changed')
      coordinate = response
      for (const item of response.items) {
        const pageId = await fieldText(item.pageId, item.source, 'pageId', checkPrincipal, offset =>
          client.page.fieldPart.query({ workspaceId: input.workspaceId, source: item.source, field: 'pageId', offset, securityRevision: response.securityRevision, head: response.head }, ...rest))
        result.push({ workspaceId: input.workspaceId, pageId, principalKind: item.principalKind, principalId: bytesToUlid(item.principalId), level: item.level })
      }
      cursor = response.next ?? undefined
    } while (cursor !== undefined)
    return result
  }
  async function levels(input: Parameters<typeof client.page.levels.query>[0], ...rest: Tail<Parameters<typeof client.page.levels.query>>) {
    const principal = cache?.userId()
    const checkPrincipal = () => { rest[0]?.signal?.throwIfAborted(); if (cache && cache.userId() !== principal) throw new RpcError(403, 'Principal changed') }
    const result: Record<string, number> = {}
    let cursor: typeof input.cursor
    let coordinate: { securityRevision: number; head: number } | undefined
    do {
      checkPrincipal()
      const response = await client.page.levels.query({ ...input, cursor }, ...rest)
      checkPrincipal()
      if (coordinate && (coordinate.securityRevision !== response.securityRevision || coordinate.head !== response.head)) throw new RpcError(403, 'Security changed')
      coordinate = response
      for (const item of response.levels) {
        const pageId = await fieldText(item.pageId, item.source, 'id', checkPrincipal, offset =>
          client.page.fieldPart.query({ workspaceId: input.workspaceId, source: item.source, field: 'id', offset, securityRevision: response.securityRevision, head: response.head }, ...rest))
        Object.defineProperty(result, pageId, { value: item.level, enumerable: true, configurable: true, writable: true })
      }
      cursor = response.next ?? undefined
    } while (cursor !== undefined)
    if (!coordinate) throw new RpcError(403, 'Missing security coordinate')
    return { ...coordinate, levels: result }
  }
  async function directory(input?: { cursor?: NonNullable<Awaited<ReturnType<typeof client.workspace.list.query>>['next']> }, ...rest: Tail<Parameters<typeof client.workspace.list.query>>) {
    const principal = cache?.userId()
    const check = () => { rest[0]?.signal?.throwIfAborted(); if (cache && cache.userId() !== principal) throw new RpcError(403, 'Principal changed') }
    type Raw = Awaited<ReturnType<typeof client.workspace.list.query>>
    type Part = { source: Parameters<typeof client.workspace.fieldPart.query>[0]['source']; field: string; offset: number; total: number; bytes: Uint8Array }
    function text(workspaceId: string, first: Part, field: 'name' | 'pageId' | 'email' | 'avatar'): Promise<string> {
      return fieldText(first, first?.source, field, check, offset =>
        client.workspace.fieldPart.query({ workspaceId, source: first.source, field, offset }, ...rest))
    }
    const workspaces = new Map<string, Omit<Raw['workspaces'][number], 'name' | 'personalRootId'> & { name: string; personalRootId: string }>()
    const memberships = new Map<string, Omit<Raw['memberships'][number], 'email' | 'name' | 'avatar'> & { email: string | null; name: string | null; avatar: string | null }>()
    const invites = new Map<string, Omit<Raw['invites'][number], 'pageId'> & { pageId: string | null }>()
    let cursor = input?.cursor
    do {
      check()
      const response = await client.workspace.list.query({ cursor }, ...rest)
      check()
      for (const row of response.workspaces) workspaces.set(row.id, { ...row, name: await text(row.id, row.name, 'name'), personalRootId: await text(row.id, row.personalRootId, 'pageId') })
      for (const row of response.memberships) memberships.set(row.id, { ...row, email: row.email === null ? null : await text(row.workspaceId, row.email, 'email'), name: row.name === null ? null : await text(row.workspaceId, row.name, 'name'), avatar: row.avatar === null ? null : await text(row.workspaceId, row.avatar, 'avatar') })
      for (const row of response.invites) invites.set(row.id, { ...row, pageId: row.pageId === null ? null : await text(row.workspaceId, row.pageId, 'pageId') })
      cursor = response.next ?? undefined
    } while (cursor !== undefined)
    check()
    return { workspaces: [...workspaces.values()], memberships: [...memberships.values()], invites: [...invites.values()] }
  }
  async function roots(input: Parameters<typeof client.page.roots.query>[0], ...rest: Tail<Parameters<typeof client.page.roots.query>>) {
    const principal = cache?.userId()
    const check = () => { rest[0]?.signal?.throwIfAborted(); if (cache && cache.userId() !== principal) throw new RpcError(403, 'Principal changed') }
    type Response = Awaited<ReturnType<typeof client.page.roots.query>>
    type Part = Awaited<ReturnType<typeof client.page.fieldPart.query>>
    function text(first: Part, source: Part['source'], field: Part['field'], coordinate: { securityRevision: number; head: number }) {
      return fieldText(first, source, field, check, offset =>
        client.page.fieldPart.query({ workspaceId: input.workspaceId, source: first.source, field, offset, ...coordinate }, ...rest))
    }
    const pages: { id: string; level: number }[] = []
    const grants: { workspaceId: string; pageId: string; principalKind: string; principalId: string; level: number }[] = []
    let cursor = input.cursor
    let grantCursor = input.grantCursor
    let rootsDone = input.rootsDone ?? false
    let grantsDone = input.grantsDone ?? false
    let coordinate: { securityRevision: number; head: number } | undefined
    do {
      check()
      const response: Response = await client.page.roots.query({ ...input, cursor, grantCursor, rootsDone, grantsDone }, ...rest)
      check()
      if (coordinate && (coordinate.securityRevision !== response.securityRevision || coordinate.head !== response.head)) throw new RpcError(403, 'Security changed')
      coordinate = { securityRevision: response.securityRevision, head: response.head }
      for (const row of response.pages) pages.push({ id: await text(row.pageId, row.source, 'id', coordinate), level: row.level })
      for (const row of response.grants) grants.push({ workspaceId: input.workspaceId, pageId: await text(row.pageId, row.source, 'pageId', coordinate), principalKind: row.principalKind, principalId: bytesToUlid(row.principalId), level: row.level })
      rootsDone ||= response.next === null
      grantsDone ||= response.grantNext === null
      cursor = response.next ?? undefined
      grantCursor = response.grantNext ?? undefined
    } while (!rootsDone || !grantsDone)
    check()
    return { pages, grants }
  }
  const page = { restoreRoot: command(client.page.restoreRoot), levelsFor: { query: (...args: Parameters<typeof client.page.levelsFor.query>) => client.page.levelsFor.query(...args) }, levels: { ...client.page.levels, query: retained('page.levels', levels) }, sharing: { ...client.page.sharing, query: sharing },
    roots: { ...client.page.roots, query: roots }, createRoot: command(client.page.createRoot), recordView: client.page.recordView, views: client.page.views, forgetMyViews: client.page.forgetMyViews }
  const workspace = { list: { ...client.workspace.list, query: retained('workspace.list', directory) }, create: committed(client.workspace.create), leave: command(client.workspace.leave), setRole: command(client.workspace.setRole), removeMember: command(client.workspace.removeMember),
    invite: command(client.workspace.invite), acceptInvite: command(client.workspace.acceptInvite), cancelInvite: command(client.workspace.cancelInvite) }
  return new Proxy({ ...client, page, workspace }, { get(_target, key) {
    if (key === 'page') return page
    if (key === 'workspace') return workspace
    return Reflect.get(client, key) as unknown
  } })
}

type Tail<T extends unknown[]> = T extends [unknown?, ...infer Rest] ? Rest : never

export function createFlashcardsRpcOptions(options: {
  client: ReturnType<typeof refreshAfterDirectoryCommands>
  queryClient: QueryClient
}) {
  const rpc = createRpcOptionsProxy(options)
  return {
    gradeLa: rpc.gradeLa,
    gradeCloze: rpc.gradeCloze,
    transcribeTrainingAudio: rpc.transcribeTrainingAudio,
    generateTts: rpc.generateTts,
    lexicon: rpc.lexicon,
    page: rpc.page,
    workspace: {
      setRole: rpc.workspace.setRole,
      leave: rpc.workspace.leave,
      removeMember: rpc.workspace.removeMember,
      list: {
        queryOptions: () => ({
          queryKey: ['workspace', 'list', undefined],
          queryFn: (ctx?: { tel?: Tel; signal?: AbortSignal }) => options.client.workspace.list.query(undefined, ctx),
          // Membership changes explicitly invalidate this server-owned directory.
          staleTime: Infinity,
        }),
      },
    },
  }
}
